// Our own personal-Telegram client (backend/tg_personal.py) — shared pieces for
// the client card and /telegram. Chats of an account are visible only to its
// owners and admin; history is pulled per chat, on demand.
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { History, Link2, RefreshCw } from "lucide-react";
import { Composer, MessageList } from "./WepsterChat";

export const errText = (e) => e?.response?.data?.detail || e?.message || "Request failed";

// tg_messages → the shape MessageList draws.
export const toListMessages = (msgs) =>
  (msgs || []).map((m) => ({
    id: m.id, direction: m.direction, created_at: m.date,
    // Group chats: show who wrote it.
    text: m.sender_name && m.direction === "in" ? `${m.sender_name}: ${m.text || ""}` : m.text,
    media: m.media ? { kind: m.media } : null,
  }));

export const accountLabel = (a) => (a ? (a.username ? `@${a.username}` : a.name || `+${a.phone}`) : "");

// Client card: the client's Telegram chats on accounts the viewer owns.
export function ClientTelegramSection({ clientId, canWrite }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null); // dialog id or null = all
  const [fromAcc, setFromAcc] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/clients/${clientId}/tg`);
      setData(r.data);
      if (!fromAcc && r.data.accounts?.length) setFromAcc(r.data.accounts[0].id);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(errText(e));
      setData(null);
    }
  };
  useEffect(() => { setData(null); setActive(null); load(); /* eslint-disable-next-line */ }, [clientId]);

  if (!data || (!data.accounts?.length && !data.dialogs?.length)) return null;

  const msgs = toListMessages((data.messages || []).filter((m) => !active || m.dialog_id === active));
  const link = async (id) => {
    try {
      await api.post(`/tg/dialogs/${encodeURIComponent(id)}/link`, { client_id: clientId });
      toast.success("Chat linked");
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const send = async (text) => {
    await api.post(`/clients/${clientId}/tg/send`, { text, account_id: fromAcc });
    load();
  };
  const accName = (id) => accountLabel((data.accounts || []).find((a) => a.id === id));

  return (
    <div className="space-y-2 pb-3 mb-3 border-b border-zinc-800" data-testid="client-tg-section">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase text-sky-400 mr-1">Telegram</span>
        {data.dialogs.length > 1 && (
          <button onClick={() => setActive(null)}
                  className={cn("text-xs px-2 py-0.5 rounded-md border",
                    !active ? "border-sky-500/50 text-sky-300 bg-sky-500/10" : "border-zinc-700 text-zinc-400")}>All</button>
        )}
        {data.dialogs.map((d) => (
          <button key={d.id} onClick={() => setActive(d.id)}
                  className={cn("text-xs px-2 py-0.5 rounded-md border",
                    active === d.id ? "border-sky-500/50 text-sky-300 bg-sky-500/10" : "border-zinc-700 text-zinc-400")}>
            {d.name || d.username || "chat"} · {accName(d.account_id) || "account"}
          </button>
        ))}
        <Button size="sm" variant="ghost" className="h-7 ml-auto text-zinc-400" onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>
      {data.suggestions?.length > 0 && canWrite && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-300 space-y-1">
          <p>Telegram chats with the same phone, not linked yet:</p>
          {data.suggestions.map((s) => (
            <div key={s.id} className="flex items-center gap-2 text-zinc-300">
              <span>{s.name || s.username} · {accName(s.account_id)}</span>
              <Button size="sm" variant="ghost" className="h-6 ml-auto text-amber-300" onClick={() => link(s.id)}>
                <Link2 className="h-3 w-3 mr-1" />Link
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="max-h-[300px] overflow-y-auto pr-1">
        <MessageList messages={msgs} emptyText={data.client_phone
          ? `No Telegram chat yet — the first message goes to +${data.client_phone}.`
          : "No Telegram chat and no phone on the card."} />
      </div>
      {canWrite && data.accounts?.length > 0 && (
        <>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span>From:</span>
            <select value={fromAcc} onChange={(e) => setFromAcc(e.target.value)}
                    className="bg-zinc-800 border border-zinc-700 text-zinc-300 h-7 rounded-md px-2 text-xs">
              {data.accounts.map((a) => <option key={a.id} value={a.id}>Telegram · {accountLabel(a)}</option>)}
            </select>
          </div>
          <Composer onSend={send} disabled={!fromAcc} />
        </>
      )}
    </div>
  );
}

// Chat pane for /telegram.
export function TgDialogPane({ dialogId, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/tg/dialogs/${encodeURIComponent(dialogId)}`);
      setData(r.data);
    } catch (e) { toast.error(errText(e)); }
  };
  useEffect(() => { setData(null); load(); /* eslint-disable-next-line */ }, [dialogId]);
  if (!data) return <p className="text-zinc-500 py-16 text-center text-sm">Loading…</p>;
  const d = data.dialog;
  const isGroup = d.kind === "group";
  const earlier = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/tg/dialogs/${encodeURIComponent(dialogId)}/history`);
      toast.success(r.data.loaded ? `Loaded ${r.data.loaded} earlier messages` : "No earlier messages");
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const send = async (text) => {
    await api.post(`/tg/dialogs/${encodeURIComponent(dialogId)}/messages`, { text });
    load(); onChanged?.();
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-zinc-50 font-medium">{d.name || "—"}</span>
        {d.username && <span className="text-xs text-zinc-400">@{d.username}</span>}
        {d.phone && <span className="text-xs text-zinc-400">+{d.phone}</span>}
        {d.is_bot && <span className="text-[10px] px-1.5 rounded bg-zinc-800 text-zinc-400">bot</span>}
        {isGroup && <span className="text-[10px] px-1.5 rounded bg-zinc-800 text-zinc-400">group · read-only</span>}
        {d.client_id && (
          <a href={`/clients?client=${d.client_id}`} className="text-xs text-emerald-400 hover:underline flex items-center gap-1">
            <Link2 className="h-3 w-3" />{d.client_name || "Client"}
          </a>
        )}
        <Button size="sm" variant="ghost" className="h-7 ml-auto text-zinc-400" disabled={busy} onClick={earlier}>
          <History className="h-3.5 w-3.5 mr-1" />Load earlier
        </Button>
      </div>
      <div className="max-h-[58vh] overflow-y-auto pr-1 border-t border-zinc-800 pt-3">
        <MessageList messages={toListMessages(data.messages)} emptyText="No messages yet — “Load earlier” pulls this chat's history" />
      </div>
      {!isGroup && <Composer onSend={send} />}
    </div>
  );
}
